import BlankSlate from "@pathwright/ui/src/components/blank/BlankSlate"
import Card from "@pathwright/ui/src/components/card/Card"
import ExecuteScripts from "@pathwright/ui/src/components/embed/ExecuteScripts"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import { useAccountQuery } from "@pathwright/graphql-client/src/generated"
import LazyAdaptedAuthContainer from "@pathwright/features/src/modules/auth/LazyAdaptedAuthContainer"
import { usePathwrightContext } from "@pathwright/features/src/modules/pathwright/PathwrightContext"
import get from "lodash/get"
import PropTypes from "prop-types"
import { useEffect } from "react"
import { useLocation } from "react-router-dom"
import styled from "styled-components"

const StyledCard = styled(Card)`
  .CardContainer {
    overflow: hidden;
  }
`

// shows a Typeform used to send Pathwright info about the upgrade/downgrade request
const PricingPlanUpgrade = ({ card, planId }) => {
  const { me, school } = usePathwrightContext()
  const { search } = useLocation()
  const requestedInterval = {
    5: "monthly",
    10: "annual"
  }[new URLSearchParams(search).get("interval")]

  const accountQuery = useAccountQuery({
    variables: {
      default_billing_plan_set: false
    }
  })

  const { billing_subscription, billing_plan_set } =
    get(accountQuery, "data.account") || {}

  // get the plan the school is upgrading/downgrading to
  const toPlan = (get(billing_plan_set, "plans") || []).find(
    (plan) => plan.id === planId
  )
  const toPlanName = get(toPlan, "name")
  const currentPlanName = get(billing_subscription, "plan.name")

  // get the Typeform hidden variables
  const typeformHiddenVariables = {
    to_plan: requestedInterval
      ? `${toPlanName} (${requestedInterval} billing)`
      : toPlanName,
    current_plan: currentPlanName,
    first_name: get(me, "first_name"),
    last_name: get(me, "last_name"),
    email: get(me, "email"),
    school_url: school.url
  }

  // massage into URL params
  const dataUrlParams = new URLSearchParams(typeformHiddenVariables)

  // Published question and choice references from Typeform p5KEj023.
  // Unknown/custom plans remain unselected rather than choosing a different plan.
  const answers = new URLSearchParams()
  const intervalAnswer = {
    monthly: "f3647056-272a-40e5-b1ee-130552e4e80d",
    annual: "17c8b03b-94ba-4545-b948-b9b5f3eded6c"
  }[requestedInterval]
  const planAnswer = {
    Starter: "243acd98-5acc-43f1-8fab-c8f972f4bb75",
    Essentials: "3c39cabe-8609-41dc-9173-58cc7eaba006",
    Complete: "6d636a14-656a-41d1-9269-46ca4ceb205b",
    Enterprise: "b88597bb-e166-4755-bf21-9f31bd096058"
  }[toPlanName]
  if (intervalAnswer)
    answers.set("answers-424b9694-840b-4346-8cfc-1dd0a2c4b03b", intervalAnswer)
  if (planAnswer)
    answers.set("answers-12302982-4756-45a7-8e30-4ecbbb28e6ae", planAnswer)

  const typeformEmbedCode = `
    <div class="typeform-widget" data-url="https://form.typeform.com/to/p5KEj023?typeform-medium=embed-snippet&${dataUrlParams.toString()}#${answers.toString()}" style="width: 100%; height: 500px;"></div> <script> (function() { var qs,js,q,s,d=document, gi=d.getElementById, ce=d.createElement, gt=d.getElementsByTagName, id="typef_orm", b="https://embed.typeform.com/"; if(!gi.call(d,id)) { js=ce.call(d,"script"); js.id=id; js.src=b+"embed.js"; q=gt.call(d,"script")[0]; q.parentNode.insertBefore(js,q) } })() </script>`

  // hack for enabling typeform to reload
  useEffect(() => {
    return () => {
      const typeformNode = document.getElementById("typef_orm")
      if (typeformNode) {
        window.typeformEmbedIsloaded = false
        typeformNode.remove()
      }
    }
  }, [])

  return (
    <StyledCard card={card} noaction>
      {accountQuery.loading ? (
        <LoadingCircle center />
      ) : !me ? (
        <LazyAdaptedAuthContainer />
      ) : !currentPlanName ? (
        <BlankSlate
          icon="x-circle"
          heading="No Active Plan"
          body="Your school is not currently activated on any Pathwright plan."
          primaryAction={{
            children: "View Plans",
            to: "/pricing/plan/"
          }}
        />
      ) : !toPlanName ? (
        <BlankSlate
          icon="x-circle"
          heading="No Plan Found"
          body={`The Pathwright plan of ID ${planId} could not be found.`}
          primaryAction={{
            children: "View Plans",
            to: "/pricing/plan/"
          }}
        />
      ) : (
        <ExecuteScripts html={typeformEmbedCode} />
      )}
    </StyledCard>
  )
}

PricingPlanUpgrade.displayName = "PricingPlanUpgrade"

PricingPlanUpgrade.propTypes = {
  planId: PropTypes.number.isRequred
}

export default PricingPlanUpgrade
